import { useState, useEffect } from 'react';
import { useApi } from '@/lib/useApi';
import { showDevNotification } from '@/lib/devToast';
import SettingsCard from '@/components/ui/SettingsCard';

// CB:BEGIN stripe-escrow-demo-page
export default function PaymentsEscrowDemo() {
  const api = useApi();

  const [projectId, setProjectId] = useState('demo_proj_1');
  const [contractorId, setContractorId] = useState('contractor_demo_1');
  const [homeownerId, setHomeownerId] = useState('homeowner_demo_1');
  const [customerId, setCustomerId] = useState<string>();
  const [totalAmountCents, setTotalAmountCents] = useState(100000); // $1000 total project
  const [status, setStatus] = useState<any>({});
  const [escrowLocal, setEscrowLocal] = useState<number>(0);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [milestones, setMilestones] = useState([
    { id: 1, name: 'Project Start', percentage: 25, amountCents: 25000, released: false },
    { id: 2, name: '50% Complete', percentage: 25, amountCents: 25000, released: false },
    { id: 3, name: 'Project Complete', percentage: 50, amountCents: 50000, released: false },
  ]);

  // Handle return from Stripe onboarding
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const onboarding = urlParams.get('onboarding');
    const contractorIdParam = urlParams.get('contractorId');
    
    if (onboarding === 'return' && contractorIdParam) {
      showDevNotification('Onboarding completed successfully');
      showDevNotification('Contractor can now receive transfers');
      showDevNotification('Try releasing a milestone payment now');

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (onboarding === 'refresh') {
      showDevNotification('Onboarding was interrupted');
      showDevNotification('Please try the onboarding process again');
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const setLoadingState = (key: string, value: boolean) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  };

  const updateMilestonePercentage = (milestoneId: number, percentage: number) => {
    setMilestones(prev => {
      const updated = prev.map(m => 
        m.id === milestoneId 
          ? { ...m, percentage, amountCents: Math.round((totalAmountCents * percentage) / 100) }
          : m
      );
      // Fix rounding errors by adjusting the last milestone
      const totalCalculated = updated.reduce((sum, m) => sum + m.amountCents, 0);
      if (totalCalculated !== totalAmountCents && updated.length > 0) {
        const lastIndex = updated.length - 1;
        updated[lastIndex].amountCents += (totalAmountCents - totalCalculated);
      }
      return updated;
    });
  };

  const updateMilestoneName = (milestoneId: number, name: string) => {
    setMilestones(prev => prev.map(m => 
      m.id === milestoneId ? { ...m, name } : m
    ));
  };

  const addMilestone = () => {
    const newId = Math.max(...milestones.map(m => m.id)) + 1;
    setMilestones(prev => [...prev, {
      id: newId,
      name: 'New Milestone',
      percentage: 10,
      amountCents: Math.round((totalAmountCents * 10) / 100),
      released: false
    }]);
  };

  const removeMilestone = (milestoneId: number) => {
    setMilestones(prev => prev.filter(m => m.id !== milestoneId));
  };

  const seedContractor = async () => {
    setLoadingState('seed', true);
    try {
      const r = await api.post('/api/demo/stripe/seed/contractor', {});
      setContractorId(r.contractorId);
      showDevNotification(`Seeded demo contractor: ${r.stripeAccountId}`);
    } catch (error) {
      console.error('Error seeding contractor:', error);
      showDevNotification('Error seeding contractor');
    } finally {
      setLoadingState('seed', false);
    }
  };

  const connectStripe = async () => {
    setLoadingState('connect', true);
    try {
      const r = await api.post('/api/demo/stripe/connect/contractor/create-link', { contractorId });
      showDevNotification(`Opening Stripe onboarding for contractor ${contractorId}...`);
      showDevNotification('🚀 IMPORTANT: Complete onboarding in the same window to enable transfers!');
      showDevNotification('📝 Use test data: SSN: 000-00-0000, Address: 123 Test St');
      // Open in same window instead of new tab to allow proper return flow
      window.location.href = r.url;
    } catch (error) {
      console.error('Error creating onboarding link:', error);
      showDevNotification('Error creating onboarding link');
    } finally {
      setLoadingState('connect', false);
    }
  };

  const startFC = async () => {
    setLoadingState('fc', true);
    try {
      const r = await api.post('/api/demo/stripe/financial-connections/session', { homeownerId, customerId });
      setCustomerId(r.customerId);
      showDevNotification(`Financial Connections created: ${r.client_secret?.slice(0, 12)}...`);
    } catch (error) {
      console.error('Error creating FC session:', error);
      showDevNotification('Error creating Financial Connections session');
    } finally {
      setLoadingState('fc', false);
    }
  };

  const depositTotal = async () => {
    setLoadingState('deposit', true);
    try {
      const r = await api.post('/api/demo/stripe/escrow/deposit', { 
        projectId, 
        homeownerId, 
        customerId, 
        amountCents: totalAmountCents 
      });
      showDevNotification(`✅ Total deposit created: $${(totalAmountCents/100).toFixed(2)} - ${r.paymentIntent?.id}`);
      showDevNotification('💰 Funds are immediately available using special test card!');
      setEscrowLocal(prev => prev + Number(totalAmountCents));
    } catch (error) {
      console.error('Error creating deposit:', error);
      showDevNotification('Error creating deposit');
    } finally {
      setLoadingState('deposit', false);
    }
  };

  const releaseMilestone = async (milestoneId: number) => {
    const milestone = milestones.find(m => m.id === milestoneId);
    if (!milestone || milestone.released) return;

    setLoadingState(`release-${milestoneId}`, true);
    try {
      const r = await api.post('/api/demo/stripe/escrow/release', { 
        projectId, 
        contractorId, 
        amountCents: milestone.amountCents 
      });
      
      if (r.error) {
        showDevNotification(`❌ ${r.error}: ${r.details || r.transferError}`);
        if (r.error === 'Contractor needs onboarding') {
          showDevNotification('🚀 ONBOARDING REQUIRED: Click "Open Stripe Onboarding" to complete setup');
          showDevNotification('📝 Use test data: SSN: 000-00-0000, Address: 123 Test St');
          showDevNotification('✅ After onboarding, transfers will work immediately!');
        }
      } else {
        showDevNotification(`✅ Milestone "${milestone.name}" released: $${(milestone.amountCents/100).toFixed(2)} - ${r.transfer?.id}`);
        showDevNotification('💰 Real money sent to contractor! Check your Stripe dashboard');
        
        // Update local state
        setMilestones(prev => prev.map(m => 
          m.id === milestoneId ? { ...m, released: true } : m
        ));
      }
    } catch (error) {
      console.error('Error releasing milestone:', error);
      showDevNotification('❌ Error releasing milestone');
    } finally {
      setLoadingState(`release-${milestoneId}`, false);
    }
  };

  const addFunds = async () => {
    setLoadingState('addFunds', true);
    try {
      const r = await api.post('/api/demo/stripe/add-funds', { amountCents: 1000000 }); // $10,000
      showDevNotification(`✅ Added $10,000 to available balance: ${r.paymentIntent?.id}`);
      showDevNotification('💰 Funds are now available for transfers using special test card!');
      showDevNotification('🚀 Use "Release Payment" buttons to see money leave your account');
    } catch (error) {
      console.error('Error adding funds:', error);
      showDevNotification('Error adding funds');
    } finally {
      setLoadingState('addFunds', false);
    }
  };

  const refreshStatus = async () => {
    setLoadingState('status', true);
    try {
      const r = await api.get('/api/demo/stripe/escrow/status', { projectId });
      setStatus(r);
    } catch (error) {
      console.error('Error refreshing status:', error);
      showDevNotification('Error refreshing status');
    } finally {
      setLoadingState('status', false);
    }
  };

  const totalReleased = milestones.filter(m => m.released).reduce((sum, m) => sum + m.amountCents, 0);
  const remainingEscrow = Math.max(0, escrowLocal - totalReleased);

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold">Demo: Milestone Payments & Escrow (Stripe Test)</h1>

      <div className="grid gap-6 md:grid-cols-3">
        <SettingsCard title="1) Contractor Connect">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input 
                value={contractorId} 
                onChange={e => setContractorId(e.target.value)} 
                placeholder="contractorId" 
                className="flex-1 px-3 py-2 border rounded-md"
              />
              <button 
                onClick={seedContractor}
                disabled={loading.seed}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {loading.seed ? 'Seeding...' : 'Seed Demo'}
              </button>
            </div>
            <button 
              onClick={connectStripe}
              disabled={loading.connect}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              {loading.connect ? 'Creating...' : 'Open Stripe Onboarding'}
            </button>
            <p className="text-xs text-gray-600">
              Use this if you want to manually onboard a contractor in test mode.
            </p>
          </div>
        </SettingsCard>

        <SettingsCard title="2) Homeowner Bank / PM">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input 
                value={homeownerId} 
                onChange={e => setHomeownerId(e.target.value)} 
                placeholder="homeownerId" 
                className="flex-1 px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                value={customerId || ''} 
                onChange={e => setCustomerId(e.target.value)} 
                placeholder="customerId (optional)" 
                className="flex-1 px-3 py-2 border rounded-md"
              />
            </div>
            <button 
              onClick={startFC}
              disabled={loading.fc}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              {loading.fc ? 'Creating...' : 'Create Financial Connections Session'}
            </button>
            <p className="text-xs text-gray-600">
              For quick tests, attach a test card PM to the customer and deposit.
            </p>
          </div>
        </SettingsCard>

        <SettingsCard title="3) Project Funding">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input 
                value={projectId} 
                onChange={e => setProjectId(e.target.value)} 
                placeholder="projectId" 
                className="flex-1 px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="number" 
                value={totalAmountCents} 
                onChange={e => {
                  const newAmount = Number(e.target.value);
                  setTotalAmountCents(newAmount);
                  // Update all milestone amounts when total changes
                  setMilestones(prev => {
                    const updated = prev.map(m => ({
                      ...m,
                      amountCents: Math.round((newAmount * m.percentage) / 100)
                    }));
                    // Fix rounding errors by adjusting the last milestone
                    const totalCalculated = updated.reduce((sum, m) => sum + m.amountCents, 0);
                    if (totalCalculated !== newAmount && updated.length > 0) {
                      const lastIndex = updated.length - 1;
                      updated[lastIndex].amountCents += (newAmount - totalCalculated);
                    }
                    return updated;
                  });
                }} 
                placeholder="Total Project Amount (cents)" 
                className="flex-1 px-3 py-2 border rounded-md"
              />
            </div>
            <button 
              onClick={depositTotal}
              disabled={loading.deposit}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              {loading.deposit ? 'Depositing...' : `Deposit Total: $${(totalAmountCents/100).toFixed(2)}`}
            </button>
            <div className="text-sm text-gray-600">
              <div>Total Escrow: <span className="font-medium">${(escrowLocal/100).toFixed(2)}</span></div>
              <div>Released: <span className="font-medium">${(totalReleased/100).toFixed(2)}</span></div>
              <div>Remaining: <span className="font-medium">${(remainingEscrow/100).toFixed(2)}</span></div>
            </div>
          </div>
        </SettingsCard>
      </div>

      {/* Milestone Payments Section */}
      <SettingsCard title="4) Milestone Payments">
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            Configure and release payments to contractor as project milestones are completed
          </div>
          
          {/* Milestone Configuration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Milestone Configuration</h4>
              <button
                onClick={addMilestone}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
              >
                + Add Milestone
              </button>
            </div>
            
            {milestones.map((milestone) => (
              <div key={milestone.id} className="p-3 border rounded-md space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={milestone.name}
                    onChange={(e) => updateMilestoneName(milestone.id, e.target.value)}
                    className="flex-1 px-2 py-1 border rounded text-sm font-medium"
                    placeholder="Milestone name"
                  />
                  <button
                    onClick={() => removeMilestone(milestone.id)}
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                  >
                    ×
                  </button>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 w-20">Percentage:</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={milestone.percentage}
                    onChange={(e) => updateMilestonePercentage(milestone.id, Number(e.target.value))}
                    className="w-20 px-2 py-1 border rounded text-sm"
                  />
                  <span className="text-sm text-gray-600">%</span>
                  <span className="text-sm font-medium ml-4">
                    ${(milestone.amountCents/100).toFixed(2)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <div className="text-xs text-gray-500">
                    Total: {milestones.reduce((sum, m) => sum + m.percentage, 0)}% of project
                  </div>
                  {milestone.released ? (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      ✅ Released
                    </span>
                  ) : (
                    <button
                      onClick={() => releaseMilestone(milestone.id)}
                      disabled={loading[`release-${milestone.id}`] || remainingEscrow < milestone.amountCents - 1}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading[`release-${milestone.id}`] ? 'Releasing...' : 'Release Payment'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex items-center gap-2 pt-2">
            <button 
              onClick={addFunds}
              disabled={loading.addFunds}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              {loading.addFunds ? 'Adding...' : 'Add $10k Available Funds'}
            </button>
            <button 
              onClick={refreshStatus}
              disabled={loading.status}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              {loading.status ? 'Refreshing...' : 'Refresh Stripe Status'}
            </button>
          </div>
          
          <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto h-48">
            {JSON.stringify(status, null, 2)}
          </pre>
        </div>
      </SettingsCard>
    </div>
  );
}
// CB:END stripe-escrow-demo-page
